import {
  IonPage,
  IonContent,
  IonIcon,
  IonLabel,
  useIonAlert,
  IonHeader,
  IonToolbar,
  IonButton,
  IonList,
  IonListHeader,
  IonSkeletonText,
  IonItem,
  IonThumbnail,
  IonCard,
  IonTitle,
  IonCardContent,
  IonImg,
  useIonLoading,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  useIonToast,
  useIonModal,
  IonModal,
  IonFooter,
  IonRow,
  IonCol,
  IonInput,
} from "@ionic/react";
import React, { useState } from "react";
import { RestApi } from "../../services/Api";
import { arrowBackOutline, calendar, checkmarkCircleOutline, saveOutline, warningOutline, cameraOutline, trashBinOutline } from "ionicons/icons";
import { useTranslation } from "react-i18next";
import { CustomerFollowUp } from "../../models/Modal";
import { loadList, PhotoWithDescription } from "../../models/Data";
import SelectOption from "../input/SelectOption";
import InputDate from "../input/Date";
import { formatDate, getNameDate, useConstructor } from "../../services/Library";
import { usePhotoGallery } from "../../services/Camera";
import { CameraSource } from "@capacitor/camera";
import "./Form.scss";

const MAX_IMAGES = 3;

const Form: React.FC<CustomerFollowUp> = (props) => {
  const { t } = useTranslation(["general"]);
  const [alert] = useIonAlert();
  const [presentLoading, dismissLoading] = useIonLoading();
  const [presentToast] = useIonToast();
  const { takePhoto } = usePhotoGallery();
  const [photosWithDesc, setPhotosWithDesc] = useState<PhotoWithDescription[]>([]);
  const [customer, setCustomer] = useState<any>([]);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [customerFollowUp, setCustomerFollowUp] = useState<any>({
    customer_id: null,
    follow_up_date: new Date().toISOString().slice(0, 19).replace('T', ' '),
    next_follow_up: null,
    status: 'waiting',
    contact_method: 'call',
    image: null,
    notes: null,
  });
  const [showDateModal, setShowDateModal] = useState<boolean>(false);
  const [showNextDateModal, setShowNextDateModal] = useState<boolean>(false);
  const [dateField, setDateField] = useState<string>("");
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

  const statusOptions = [
    { value: 'interested', label: t('status_interested') },
    { value: 'negotiation', label: t('status_negotiation') },
    { value: 'waiting', label: t('status_waiting') },
  ];

  const contactMethodOptions = [
    { value: 'call', label: t('call') },
    { value: 'massage', label: t('massage') },
  ];

  const massageAlert = async (status: string, body: string) => {
    alert({
      header: t(status),
      message: body,
      cssClass: `header-alert-${status}`,
      buttons: [{ cssClass: "alert-button-confirm", text: "Ok" }],
    });
  };

  const getFormData = async () => {
    setLoaded(false);
    await RestApi(`customer-follow-up/create`, "get", {}, "application/json")
      .then((res: any) => {
        if (res.data.code === 200) {
          setCustomer(res.data.data.customers || []);
          setLoaded(true);
        } else {
          massageAlert('error', res.data.message);
          setLoaded(true);
        }
      })
      .catch((err: any) => {
        massageAlert('error', err.message);
        setLoaded(true);
      });
  };

  const getEditData = async () => {
    setLoaded(false);
    await RestApi(`customer-follow-up/${props.idCustomerFollowUp}/edit`, "get", {}, "application/json")
      .then((res: any) => {
        if (res.data.code === 200) {
          const data = res.data.data.customerFollowUp;
          setCustomerFollowUp({
            customer_id: data.customer_id,
            follow_up_date: data.follow_up_date ? data.follow_up_date.replace('T', ' ').slice(0, 19) : null,
            next_follow_up: data.next_follow_up ? data.next_follow_up.replace('T', ' ').slice(0, 19) : null,
            status: data.status,
            contact_method: data.contact_method,
            image: data.image,
            notes: data.notes,
          });
          setSelectedCustomer(data.customer_id);
          setCustomer(res.data.data.customers || []);
          if (data.images && Array.isArray(data.images) && data.images.length > 0) {
            setPhotosWithDesc(
              data.images.map((img: any) => ({
                photo: { webviewPath: img.image },
                description: img.description,
              }))
            );
          }
          setLoaded(true);
        } else {
          massageAlert('error', res.data.message);
          setLoaded(true);
        }
      })
      .catch((err: any) => {
        massageAlert('error', err.message);
        setLoaded(true);
      });
  };

  const handleSubmit = async () => {
    if (!customerFollowUp.customer_id) {
      presentToast({
        message: t("selectCustomerRequired"),
        duration: 2500,
        color: "danger",
        icon: warningOutline,
      });
      return;
    }

    if (!customerFollowUp.follow_up_date) {
      presentToast({
        message: t("followUpDateRequired"),
        duration: 2500,
        color: "danger",
        icon: warningOutline,
      });
      return;
    }

    presentLoading({
      message: props.type === "create" ? t("saving") : t("updating"),
    });

    try {
      const FormData = global.FormData;
      let formData = new FormData();
      formData.append('customer_id', customerFollowUp.customer_id);
      formData.append('follow_up_date', formatDate(customerFollowUp.follow_up_date));
      formData.append('next_follow_up', customerFollowUp.next_follow_up ? formatDate(customerFollowUp.next_follow_up) : "");
      formData.append('status', customerFollowUp.status);
      formData.append('contact_method', customerFollowUp.contact_method);
      formData.append('notes', customerFollowUp.notes);
      for (let i = 0; i < photosWithDesc.length; i++) {
        const pwDesc = photosWithDesc[i];
        if (pwDesc.photo) {
          formData.append(`images[${i}]`, pwDesc.photo.blobData ?? pwDesc.photo.webviewPath); 
        }
        formData.append(
          `description[${i}]`,
          pwDesc.description ?? "",
        );
      }
      const endpoint = props.type === "create"
        ? "customer-follow-up"
        : `customer-follow-up/${props.idCustomerFollowUp}`;
      await RestApi(
        endpoint,
        `post`,
        formData,
        "application/x-www-form-urlencoded",
      )
        .then((res: any) => {
          dismissLoading();
          if (res.data.code === 200) {
            presentToast({
              message: res.data.message || t("dataSaved"),
              duration: 2500,
              color: "success",
              icon: checkmarkCircleOutline,
            });
            props.onDismiss();
          } else {
            presentToast({
              message: res.data.message,
              duration: 2500,
              color: "danger",
              icon: warningOutline,
            });
          }
        })
        .catch((err: any) => {
          dismissLoading();
          massageAlert("error", err.message);
        });
    } catch (error: any) {
      dismissLoading();
      massageAlert('error', error.message);
    }
  };

  const handleDateSelect = (date: string) => {
    if (dateField === "follow_up_date") {
      setCustomerFollowUp((prev: any) => ({
        ...prev,
        follow_up_date: date,
      }));
    } else if (dateField === "next_follow_up") {
      setCustomerFollowUp((prev: any) => ({
        ...prev,
        next_follow_up: date,
      }));
    }
    setShowDateModal(false);
    setShowNextDateModal(false);
  };

  const handleCustomerSelect = (customerId: string) => {
    setSelectedCustomer(customerId);
    setCustomerFollowUp((prev: any) => ({
      ...prev,
      customer_id: customerId,
    }));
    dismissCustomerSelect();
  };

  const [presentCustomerSelect, dismissCustomerSelect] = useIonModal(SelectOption, {
    title: t("module.customer"),
    items: customer.map((c: any) => ({
      value: c.customer_id,
      text: `${c.customer_name} (${c.customer_id})`,
    })),
    selectedItems: selectedCustomer ? [selectedCustomer] : [],
    multiple: false,
    onSelectionChange: handleCustomerSelect,
    onSelectionCancel: () => dismissCustomerSelect(),
  });

  useConstructor(() => {
    if (props.type === "edit" && props.idCustomerFollowUp) {
      getEditData();
    } else {
      getFormData();
    }
  });

  const handleAddPhoto = async () => {
    if (photosWithDesc.length >= MAX_IMAGES) {
      presentToast({
        message: t("maxImagesUpload", { count: MAX_IMAGES }) || `Maximum ${MAX_IMAGES} images allowed`,
        duration: 2500,
        color: "warning",
        icon: warningOutline,
      });
      return;
    }
    const photo = await takePhoto(CameraSource.Photos);
    if (photo && photo.webviewPath) {
      setPhotosWithDesc((prev) => [
        ...prev,
        { photo, description: "" }
      ]);
    }
  };

  const handleDeletePhoto = (index: number) => {
    setPhotosWithDesc((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleDescriptionChange = (index: number, desc: string) => {
    setPhotosWithDesc((prev) =>
      prev.map((item, idx) =>
        idx === index
          ? { ...item, description: desc }
          : item
      )
    );
  };

  return (
    <IonPage>
      <IonHeader className="toolbar-form">
        <IonToolbar className="ion-no-padding">
          <IonButton
            color="light"
            slot="start"
            fill="clear"
            onClick={() => props.onDismiss()}
          >
            <IonIcon color="light" src={arrowBackOutline} />
          </IonButton>
          <IonTitle color={"light"}>
            <h2>
              {props.type === "create" ? t("create") : t("edit")}{" "}
              {t("module.customer-follow-up")}
            </h2>
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="form-content">
        {!loaded && (
          <IonList>
            <IonListHeader>
              <IonSkeletonText
                animated={true}
                style={{ width: "80px" }}
              ></IonSkeletonText>
            </IonListHeader>
            {loadList.map((value: any, key: any) => (
              <IonItem key={key}>
                <IonThumbnail slot="start">
                  <IonSkeletonText animated={true}></IonSkeletonText>
                </IonThumbnail>
                <IonLabel>
                  <h3>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "80%" }}
                    ></IonSkeletonText>
                  </h3>
                  <p>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "60%" }}
                    ></IonSkeletonText>
                  </p>
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
        )}
        {loaded && (
          <IonList>
            <IonRow>
              <IonCol size="6">
                <IonItem
                  button
                  onClick={() =>
                    presentCustomerSelect({ cssClass: "modal-fullscreen" })
                  }
                >
                  <IonInput
                    label={t("module.customer") + " *"}
                    labelPlacement="floating"
                    placeholder={t("selectCustomer")}
                    value={
                      selectedCustomer
                        ? customer.find(
                            (c: any) => c.customer_id === selectedCustomer,
                          )?.customer_name || selectedCustomer
                        : ""
                    }
                    readonly
                  ></IonInput>
                </IonItem>
              </IonCol>
              <IonCol size="6">
                <IonItem
                  button
                  onClick={() => {
                    if (props.type === "create") {
                      setDateField("follow_up_date");
                      setShowDateModal(true);
                    }
                  }}
                >
                  <IonInput
                    label={t("followUpDate") + " *"}
                    labelPlacement="floating"
                    placeholder={t("selectDate")}
                    value={
                      customerFollowUp.follow_up_date
                        ? getNameDate(customerFollowUp.follow_up_date)
                        : ""
                    }
                    readonly
                  />
                  <IonIcon src={calendar} slot="end" />
                </IonItem>
              </IonCol>
              <IonCol size="6">
                <IonItem
                  button
                  onClick={() => {
                    setDateField("next_follow_up");
                    setShowNextDateModal(true);
                  }}
                >
                  <IonInput
                    label={t("nextFollowUp")}
                    labelPlacement="floating"
                    placeholder={t("selectDate") + " " + t("optional")}
                    value={
                      customerFollowUp.next_follow_up
                        ? getNameDate(customerFollowUp.next_follow_up)
                        : ""
                    }
                    readonly
                  />
                  <IonIcon src={calendar} slot="end" />
                </IonItem>
              </IonCol>
              <IonCol size="6">
                <IonItem>
                  <IonSelect
                    label={t("status") + " *"}
                    labelPlacement="floating"
                    value={customerFollowUp.status}
                    placeholder={t("selectStatus")}
                    onIonChange={(e) =>
                      setCustomerFollowUp((prev: any) => ({
                        ...prev,
                        status: e.detail.value,
                      }))
                    }
                  >
                    {statusOptions.map((option) => (
                      <IonSelectOption key={option.value} value={option.value}>
                        {option.label}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </IonItem>
              </IonCol>
              <IonCol size="6">
                <IonItem>
                  <IonSelect
                    label={t("contactMethod") + " *"}
                    labelPlacement="floating"
                    value={customerFollowUp.contact_method}
                    placeholder={t("selectContactMethod")}
                    onIonChange={(e) =>
                      setCustomerFollowUp((prev: any) => ({
                        ...prev,
                        contact_method: e.detail.value,
                      }))
                    }
                  >
                    {contactMethodOptions.map((option) => (
                      <IonSelectOption key={option.value} value={option.value}>
                        {option.label}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </IonItem>
              </IonCol>
              <IonCol size="6">
                <IonItem>
                  <IonTextarea
                    label={t("note")}
                    labelPlacement="floating"
                    placeholder={t("notesPlaceholder")}
                    rows={0}
                    value={customerFollowUp.notes}
                    onIonInput={(e: any) =>
                      setCustomerFollowUp((prev: any) => ({
                        ...prev,
                        notes: e.detail.value,
                      }))
                    }
                  />
                </IonItem>
              </IonCol>
            </IonRow>

            <IonRow>
              <IonCol size="12">
                <IonCard>
                  <IonCardContent>
                    <IonRow>
                      <IonCol size="12">
                        {photosWithDesc.length === 0 && (
                          <IonImg
                            src="assets/img/image_not_available.png"
                            style={{
                              width: "100%",
                              height: 120,
                              objectFit: "contain",
                            }}
                          />
                        )}
                        {photosWithDesc.map((item, idx) => (
                          <IonRow
                            key={idx}
                            style={{
                              marginBottom: 24,
                              border: "1px solid #e0e0e0",
                              borderRadius: 8,
                              padding: 8,
                              background: "#fafafa",
                              alignItems: "center",
                            }}
                          >
                            <IonCol
                              size="auto"
                              style={{
                                flex: "0 0 120px",
                                maxWidth: 120,
                                marginRight: 12,
                              }}
                            >
                              <IonImg
                                src={item.photo.webviewPath}
                                style={{
                                  width: "100px",
                                  height: "100px",
                                  objectFit: "cover",
                                  borderRadius: 8,
                                }}
                              />
                            </IonCol>
                            <IonCol>
                              <IonTextarea
                                label={t("description")}
                                labelPlacement="floating"
                                placeholder={t("notesPlaceholder")}
                                value={item.description}
                                onIonInput={(e) =>
                                  handleDescriptionChange(
                                    idx,
                                    e.detail.value || "",
                                  )
                                }
                                rows={2}
                                autoGrow={true}
                              />
                            </IonCol>
                            <IonCol
                              size="auto"
                              style={{ textAlign: "right", marginTop: 8 }}
                            >
                              <IonIcon
                                color="danger"
                                size="medium"
                                src={trashBinOutline}
                                onClick={() => handleDeletePhoto(idx)}
                              />
                            </IonCol>
                          </IonRow>
                        ))}
                        {photosWithDesc.length < MAX_IMAGES && (
                          <IonButton
                            size="small"
                            style={{
                              fontSize: "12px",
                              height: "28px",
                              minHeight: 0,
                              width: "100%",
                            }}
                            fill="solid"
                            color="medium"
                            onClick={handleAddPhoto}
                          >
                            <IonIcon
                              src={cameraOutline}
                              slot="start"
                              style={{ fontSize: "14px" }}
                            />
                            {t("add")} {t("image")}
                          </IonButton>
                        )}

                        <div
                          style={{
                            color: "#666",
                            fontSize: "13px",
                            paddingTop: 10,
                            marginTop: 6,
                          }}
                        >
                          {t("max")} {MAX_IMAGES} {t("image")}
                        </div>
                      </IonCol>
                    </IonRow>
                  </IonCardContent>
                </IonCard>
              </IonCol>
            </IonRow>
          </IonList>
        )}

        {showDateModal && (
          <IonModal
            isOpen={showDateModal}
            onDidDismiss={() => setShowDateModal(false)}
            initialBreakpoint={0.6}
            breakpoints={[0, 1.0]}
            handleBehavior="cycle"
            backdropDismiss={true}
          >
            <InputDate
              onCloseModal={handleDateSelect}
              initialData={customerFollowUp.follow_up_date?.split(" ")[0]}
              isOpen={showDateModal}
              min={new Date().toISOString()}
            />
          </IonModal>
        )}

        {showNextDateModal && (
          <IonModal
            isOpen={showNextDateModal}
            onDidDismiss={() => setShowNextDateModal(false)}
            initialBreakpoint={0.6}
            breakpoints={[0, 1.0]}
            handleBehavior="cycle"
          >
            <InputDate
              onCloseModal={handleDateSelect}
              initialData={customerFollowUp.next_follow_up?.split(" ")[0]}
              isOpen={showNextDateModal}
              min={new Date().toISOString()}
            />
          </IonModal>
        )}
      </IonContent>
      <IonFooter>
        <IonButton expand="block" color="danger" onClick={handleSubmit}>
          <IonIcon color="light" src={saveOutline} />
          {t("save")}
        </IonButton>
      </IonFooter>
    </IonPage>
  );
};

export default Form;